import apiV2 from '../../api/v2';
import { buildClinicalFilename, extractClinicalPatientFileId } from './clinical-filenames';

// Upper bound on SDI versions fetched for the version selector — generous
// enough that no real case hits it.
const SDI_VERSION_LIMIT = 50;

export interface SdiVersion {
    /** Timeline event id. */
    id: string;
    /** Monotonically increasing version number (1, 2, 3...). */
    version: number;
    /** ISO timestamp the version was created. */
    createdAt: string;
    /** GCS-stored PDF file id; resolved to a download URL on demand. */
    patientFileId: string;
    /** Filename for display (e.g. "SDI-Wis_Robby-2026-05-12-v2.pdf"). */
    fileName: string;
}

interface RawTimelineEvent {
    id: string;
    version: number;
    status: string;
    createdAt: string;
    payload?: Record<string, unknown>;
}

function buildSdiFilename(payload: Record<string, unknown> | undefined, version: number): string {
    return buildClinicalFilename('SDI', 'sdiPdf', payload, version);
}

function extractSdiPatientFileId(payload: Record<string, unknown> | undefined): string | null {
    return extractClinicalPatientFileId('sdiPdf', payload);
}

/**
 * List all SDI submission versions for a patient. Returns latest-first
 * (highest version number) so callers can default-select the head of the list.
 *
 * Only `status='active'` events are surfaced. Events whose payload lacks a
 * `patientFileId` are excluded (can't be displayed in the PDF viewer).
 */
export async function fetchSdiVersions(patientId: string): Promise<SdiVersion[]> {
    const response = await apiV2.get<{ data: RawTimelineEvent[] }>(
        `/patients/${encodeURIComponent(patientId)}/timeline`,
        { params: { type: 'sdi_submission', limit: SDI_VERSION_LIMIT } }
    );
    const events = response.data?.data ?? [];
    return (
        events
            .filter((e) => e.status === 'active')
            .map((e) => {
                const patientFileId = extractSdiPatientFileId(e.payload);
                if (!patientFileId) return null;
                return {
                    id: e.id,
                    version: e.version,
                    createdAt: e.createdAt,
                    patientFileId,
                    fileName: buildSdiFilename(e.payload, e.version)
                };
            })
            .filter((v): v is SdiVersion => v !== null)
            // Documents imported off Monday all carry version 0, so the version number
            // alone cannot order them — fall back to the date they were filed.
            .sort((a, b) => b.version - a.version || b.createdAt.localeCompare(a.createdAt))
    );
}
